import type { WarmRequest, WorkerMessage } from "../worker/messages";
import type { WorkerLike } from "../worker/worker-host";

/**
 * The tab's drafting workers (RFC 0002 §5.1). A warm-up starts the one Open Draft will use before anyone asks for it,
 * and that worker fetches and compiles the engine's WebAssembly while the person is still on another page; the
 * engine then claims it and finds the engine loaded, or loading. The compiled module the first worker announces is
 * kept for the tab's life, so a later worker (after the person switches organisation, say) starts from it without
 * fetching or compiling again.
 */

/** A real worker: its messages and errors can be listened to beside the host's own handler. */
export interface DraftWorkerLike extends WorkerLike {
  addEventListener(type: "message", listener: (event: MessageEvent<WorkerMessage>) => void): void;
  addEventListener(type: "error", listener: (event: Event) => void): void;
}

export class DraftWorkers {
  private readonly create: () => DraftWorkerLike;
  /** Warmed and not yet claimed. */
  private spare: DraftWorkerLike | undefined;
  private claims = 0;
  private module: WebAssembly.Module | undefined;
  private readonly broken = new WeakSet<DraftWorkerLike>();

  constructor(create: () => DraftWorkerLike) {
    this.create = create;
  }

  /** Whether a worker is warming or the engine has started: a warm-up then has nothing to do. */
  get warm(): boolean {
    return this.spare !== undefined || this.claims > 0;
  }

  /** The module a worker compiled, once one announced it. */
  get compiled(): WebAssembly.Module | undefined {
    return this.module;
  }

  /**
   * Starts the worker the engine will claim, loading the engine at `priority`; whether it did. A no-op once a worker
   * is warming or the engine has started.
   */
  warmUp(priority: RequestPriority): boolean {
    if (this.warm) return false;
    this.spare = this.spawn({ module: this.module, priority });
    return true;
  }

  /** The worker for the tab's engine: the warmed one when it is sound, else a new one. */
  claim(): DraftWorkerLike {
    const spare = this.spare;
    this.spare = undefined;
    this.claims += 1;
    if (spare !== undefined && !this.broken.has(spare)) return spare;
    spare?.terminate();
    // With the tab's module the new worker only instantiates it; without, `init` loads the engine as it always has.
    return this.spawn(this.module === undefined ? undefined : { module: this.module });
  }

  private spawn(request: WarmRequest | undefined): DraftWorkerLike {
    const worker = this.create();
    worker.addEventListener("message", (event) => {
      if (event.data.kind === "warmed") this.module ??= event.data.module;
    });
    worker.addEventListener("error", () => this.broken.add(worker));
    if (request !== undefined) worker.postMessage({ kind: "warm", ...request }, []);
    return worker;
  }
}

/** The tab's workers, each started from the drafting worker's entry. */
export const draftWorkers = new DraftWorkers(
  () =>
    new Worker(new URL("../worker/draft.worker.ts", import.meta.url), {
      type: "module",
      name: "orthos-draft",
    }),
);
